/* eslint-disable react/require-default-props */
import React, { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { ConversationArrowRight, TemplateIcon } from '~/assets/svgIcons';
import * as lib from '~/dependencies';
import { ChannelTypeEnum } from '~/enums/conversations';
import { SmsTemplate } from '~/types/accountInfo';
import { checkGSMAlphabet, countLineBreaks, eventTracker } from '~/helpers';
import { CircularProgress } from '@material-ui/core';
import Templates from './components/Templates';
import {
  ArrowIcon,
  Bold,
  Button,
  Container,
  Input,
  Row,
  SendButton,
  WarnMessage,
} from './styles';

/**
 * Inserts text at the current cursor location of a textarea or input element.
 * @param inputRef Reference to the input or textarea element
 * @param text Text to be inserted
 */
function insertTextAtCursorLocation(inputRef: any, text: string) {
  const start = inputRef.selectionStart;
  const end = inputRef.selectionEnd;

  inputRef.value =
    inputRef.value.slice(0, start) + text + inputRef.value.slice(end);

  // Move cursor after inserted text
  const newPosition = start + text.length;
  inputRef.selectionStart = newPosition;
  inputRef.selectionEnd = newPosition;
}

type MessageTextInputProps = {
  onSend: (message: string) => void;
  /**
   * Emitted when the user add a template with usage '10dlc'
   * @param template returns the selected template
   */
  onTemplateAppend?: (template: SmsTemplate) => void;
  channelType: ChannelTypeEnum;
  disabled?: boolean;
  isLoading?: boolean;
};

export default function TextInput({
  onSend,
  onTemplateAppend,
  channelType,
  disabled,
  isLoading,
}: MessageTextInputProps) {
  // REF's
  const textareaRef = useRef(null);

  // HOOK's
  const { t } = lib.useTranslation();

  // STATE's
  const [maxTextLng, setMaxTextLng] = useState<boolean>(false);
  const [invalidChars, setInvalidChars] = useState<string[]>([]);
  const [message, setMessage] = useState<string>('');
  const [openTemplates, setOpenTemplates] = useState<boolean>(false);

  // CONST's
  const MAX_SMS_MSG_LNG = 960;
  const hasError = maxTextLng || invalidChars.length > 0;

  const isDisabled = disabled || hasError || message?.length < 1;

  const validateSMSMessage = (msg: string) => {
    const invalidCharsList = checkGSMAlphabet(msg);

    if (invalidCharsList.length > 0) {
      setInvalidChars([...invalidCharsList]);
    } else {
      setInvalidChars([]);
    }

    setMaxTextLng(msg.length > MAX_SMS_MSG_LNG);

    setMessage(msg);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const validateWhatsAppMessage = (msg: string) => {
    // TODO:
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const validateInternalMessage = (msg: string) => {
    // TODO:
  };

  const validatorFactory = (type: ChannelTypeEnum) => {
    switch (type) {
      case ChannelTypeEnum.SMS:
        return { validate: (msg: string) => validateSMSMessage(msg) };
      case ChannelTypeEnum.WHATSAPP:
        return { validate: (msg: string) => validateWhatsAppMessage(msg) };
      case ChannelTypeEnum.INTERNAL:
        return { validate: (msg: string) => validateInternalMessage(msg) };
      default:
        return { validate: (msg: string) => validateSMSMessage(msg) };
    }
  };

  const validator = validatorFactory(channelType);

  const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const textarea = textareaRef.current;

    // @ts-ignore
    textarea.style.height = 'auto';
    // @ts-ignore
    // eslint-disable-next-line prefer-template
    textarea.style.height = textarea.scrollHeight + 'px';

    validator.validate(e.currentTarget.value);
  };

  const handleOnTemplateChange = (template: SmsTemplate) => {
    const textarea = textareaRef.current;

    if (textarea) {
      const { messageText: templateMessage } = template;

      if (onTemplateAppend) {
        onTemplateAppend(template);
      }

      // Line height based on current font size font-size: 16px;
      const fontLineHeight = 22.5;
      const updatedMessage = `${message} ${templateMessage}`;
      const lineCount = countLineBreaks(updatedMessage);

      // @ts-ignore
      textarea.style.height = 'auto';
      // @ts-ignore
      textarea.style.height = `${lineCount * fontLineHeight}px`;

      insertTextAtCursorLocation(textarea, templateMessage);

      // This update will render immediately
      flushSync(() => {
        setOpenTemplates(false);
        // @ts-ignore
        setMaxTextLng(textarea.value.length > MAX_SMS_MSG_LNG);

        // @ts-ignore
        setMessage(textarea.value);

        // @ts-ignore
        setTimeout(() => textarea.focus(), 300);
      });
    }
  };

  const handleSend = () => {
    if (!textareaRef.current) return;

    // @ts-ignore
    const msg = textareaRef.current.value;

    if (onSend && msg.length > 0) {
      onSend(msg);
      // @ts-ignore
      textareaRef.current.value = '';
      // @ts-ignore
      textareaRef.current.style.height = 'auto';
      // @ts-ignore
      textareaRef.current.height = '0px';
    }

    // @ts-ignore
    eventTracker(events => events.SendSMSClicked);
  };

  const handleOpenTemplates = () => {
    setOpenTemplates(!openTemplates);
  };

  return (
    <Container hasError={hasError}>
      {hasError ? (
        <WarnMessage hasError={hasError}>
          {maxTextLng ? (
            <span>
              {t('screens.conversation.smsMaxCharLng')}{' '}
              <Bold>
                {message.length}/{MAX_SMS_MSG_LNG}
              </Bold>
              .
            </span>
          ) : null}
          {invalidChars.length > 0 ? (
            <span>
              {t('screens.conversation.smsInvalidChars')}{' '}
              <Bold>{invalidChars.toString()}</Bold>
            </span>
          ) : null}
        </WarnMessage>
      ) : null}
      <Row>
        <Button
          src={TemplateIcon}
          title="Templates"
          onClick={handleOpenTemplates}
        />
        <Input
          id="input-message"
          rows={1}
          ref={textareaRef}
          placeholder={t('screens.conversation.txtInputText')}
          onInput={handleInput}
        />
        <SendButton
          id="send-message-button"
          type="button"
          onClick={handleSend}
          disabled={isLoading || isDisabled}>
          {isLoading ? (
            <CircularProgress
              color="inherit"
              style={{ width: 15, height: 15 }}
            />
          ) : (
            <ArrowIcon src={ConversationArrowRight} />
          )}
        </SendButton>
      </Row>
      {openTemplates && (
        <Templates
          onClose={handleOpenTemplates}
          onTemplateChange={handleOnTemplateChange}
        />
      )}
    </Container>
  );
}
